import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { ArrowLeft, Users, CreditCard, BarChart3, Shield, AlertTriangle, Search, Ban, Check, X, FileText, Activity } from 'lucide-react';

type Props = {
  onBack: () => void;
};

type AgencyRow = {
  id: string;
  name: string;
  slug: string;
  email: string | null;
  subscription_status: string | null;
  subscription_plan: string | null;
  created_at: string;
};

type AdminTab = 'overview' | 'agencies' | 'subscriptions' | 'reports' | 'moderation';

export default function AdminDashboard({ onBack }: Props) {
  const [tab, setTab] = useState<AdminTab>('overview');
  const [agencies, setAgencies] = useState<AgencyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({ total: 0, active: 0, canceled: 0, totalApps: 0 });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    const { data: agencyData } = await supabase
      .from('agencies')
      .select('id, name, slug, email, subscription_status, subscription_plan, created_at')
      .order('created_at', { ascending: false });
    setAgencies(agencyData ?? []);

    const { count: appCount } = await supabase
      .from('applications')
      .select('*', { count: 'exact', head: true });

    setStats({
      total: agencyData?.length ?? 0,
      active: agencyData?.filter((a) => a.subscription_status === 'active').length ?? 0,
      canceled: agencyData?.filter((a) => a.subscription_status === 'canceled').length ?? 0,
      totalApps: appCount ?? 0,
    });
    setLoading(false);
  }

  async function toggleSubscriptionStatus(agencyId: string, currentStatus: string | null) {
    const newStatus = currentStatus === 'canceled' ? 'active' : 'canceled';
    await supabase
      .from('agencies')
      .update({ subscription_status: newStatus, updated_at: new Date().toISOString() })
      .eq('id', agencyId);
    loadData();
  }

  const filtered = agencies.filter(
    (a) => a.name.toLowerCase().includes(search.toLowerCase()) || a.email?.toLowerCase().includes(search.toLowerCase())
  );

  const NAV: { id: AdminTab; label: string; icon: typeof Users }[] = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'agencies', label: 'Agencies', icon: Users },
    { id: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'moderation', label: 'Moderation', icon: Shield },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={onBack} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-900 text-sm font-medium transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </button>
          <div className="flex items-center gap-2 ml-auto">
            <div className="w-7 h-7 bg-slate-900 rounded-lg flex items-center justify-center">
              <Shield className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="font-bold text-gray-900 text-sm">Admin Dashboard</span>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white border border-gray-200 rounded-md p-1 overflow-x-auto">
          {NAV.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${
                tab === id ? 'bg-slate-900 text-white' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : tab === 'overview' ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Total Agencies', value: stats.total, icon: Users, color: 'blue' },
                { label: 'Active Subscriptions', value: stats.active, icon: Check, color: 'green' },
                { label: 'Canceled', value: stats.canceled, icon: X, color: 'red' },
                { label: 'Total Applications', value: stats.totalApps, icon: FileText, color: 'blue' },
              ].map(({ label, value, icon: Icon, color }) => (
                <div key={label} className="bg-white border border-gray-200 rounded-lg p-5">
                  <div className={`w-10 h-10 bg-${color}-50 rounded-md flex items-center justify-center mb-3`}>
                    <Icon className={`w-5 h-5 text-${color}-600`} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{value}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{label}</p>
                </div>
              ))}
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-sm font-bold text-gray-900 mb-4">Recent Agencies</h3>
              <div className="space-y-2">
                {agencies.slice(0, 5).map((a) => (
                  <div key={a.id} className="flex items-center gap-3 py-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-700 font-bold text-xs">{a.name.charAt(0)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{a.name}</p>
                      <p className="text-xs text-gray-400 truncate">{a.email}</p>
                    </div>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                      a.subscription_status === 'active' ? 'bg-green-100 text-green-700' :
                      a.subscription_status === 'canceled' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {a.subscription_status ?? 'unknown'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : tab === 'agencies' || tab === 'subscriptions' ? (
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search agencies..."
                className="w-full pl-11 pr-4 py-3 rounded-md border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              />
            </div>

            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Agency</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Email</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Joined</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((a) => (
                    <tr key={a.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-blue-700 font-bold text-xs">{a.name.charAt(0)}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{a.name}</p>
                            <p className="text-xs text-gray-400 truncate">/{a.slug}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 hidden sm:table-cell truncate max-w-[200px]">{a.email}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                          a.subscription_status === 'active' ? 'bg-green-100 text-green-700' :
                          a.subscription_status === 'canceled' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {a.subscription_status ?? 'unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 hidden sm:table-cell">
                        {new Date(a.created_at).toLocaleDateString('en-GB')}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => toggleSubscriptionStatus(a.id, a.subscription_status)}
                          className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${
                            a.subscription_status === 'canceled'
                              ? 'bg-green-50 text-green-700 hover:bg-green-100'
                              : 'bg-red-50 text-red-600 hover:bg-red-100'
                          }`}
                        >
                          {a.subscription_status === 'canceled' ? 'Activate' : 'Cancel'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <div className="text-center py-12 text-sm text-gray-400">No agencies found.</div>
              )}
            </div>
          </div>
        ) : tab === 'reports' ? (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Platform Analytics</h3>
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-md p-4">
                  <p className="text-xs text-gray-500 mb-1">Total Revenue (Monthly)</p>
                  <p className="text-2xl font-bold text-gray-900">${stats.active * 70}</p>
                  <p className="text-xs text-gray-400 mt-1">{stats.active} active × $50</p>
                </div>
                <div className="bg-gray-50 rounded-md p-4">
                  <p className="text-xs text-gray-500 mb-1">Conversion Rate</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.total > 0 ? Math.round((stats.active / stats.total) * 100) : 0}%
                  </p>
                  <p className="text-xs text-gray-400 mt-1">{stats.active} of {stats.total} agencies</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-md p-4">
                <p className="text-xs text-gray-500 mb-1">Applications per Agency (avg)</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.total > 0 ? Math.round(stats.totalApps / stats.total) : 0}
                </p>
              </div>
            </div>
          </div>
        ) : tab === 'moderation' ? (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-900">Content Moderation</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Monitor agencies for compliance with the Acceptable Use Policy. Use the search to find agencies by name or email.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-sm font-bold text-gray-900 mb-4">Platform Security</h3>
              <div className="space-y-3">
                {[
                  { label: 'HTTPS / SSL Encryption', status: 'Active' },
                  { label: 'Row-Level Security (Database)', status: 'Active' },
                  { label: 'Password Hashing (bcrypt)', status: 'Active' },
                  { label: 'API Rate Limiting', status: 'Active' },
                  { label: 'File Upload Limits (10MB)', status: 'Active' },
                  { label: 'Spam Protection', status: 'Active' },
                ].map(({ label, status }) => (
                  <div key={label} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <div className="flex items-center gap-2">
                      <Activity className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-700">{label}</span>
                    </div>
                    <span className="text-xs font-semibold px-2.5 py-1 bg-green-100 text-green-700 rounded-full">{status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
